/** @format — Auto-save / restore unsent chat composer text per conversation */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  CHAT_DRAFT_DEBOUNCE_MS,
  clearChatDraft,
  readChatDraft,
  writeChatDraft,
} from "@/utils/chatDraftStorage";

/**
 * @param {string | null | undefined} conversationId — Unique thread key (chat id, shp_panel_*, etc.)
 */
export function useChatDraft(conversationId) {
  const [input, setInputState] = useState(() => readChatDraft(conversationId));
  const inputRef = useRef(input);
  const debounceRef = useRef(null);
  const conversationRef = useRef(conversationId);

  inputRef.current = input;

  const flushDraft = useCallback((id, text) => {
    if (!id) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    writeChatDraft(id, text);
  }, []);

  useEffect(() => {
    const prevId = conversationRef.current;
    if (prevId !== conversationId) {
      flushDraft(prevId, inputRef.current);
      conversationRef.current = conversationId;
      setInputState(readChatDraft(conversationId));
    }
  }, [conversationId, flushDraft]);

  useEffect(() => {
    return () => {
      flushDraft(conversationRef.current, inputRef.current);
    };
  }, [flushDraft]);

  const setInput = useCallback(
    (value) => {
      const next =
        typeof value === "function" ? value(inputRef.current) : value ?? "";
      setInputState(next);
      const id = conversationRef.current;
      if (!id) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        writeChatDraft(id, next);
        debounceRef.current = null;
      }, CHAT_DRAFT_DEBOUNCE_MS);
    },
    [],
  );

  const clearDraft = useCallback(() => {
    const id = conversationRef.current;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setInputState("");
    if (id) clearChatDraft(id);
  }, []);

  return { input, setInput, clearDraft };
}
